// UPost.com - Forgot Password Page
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { Eye, EyeOff, Mail, Shield, CheckCircle } from 'lucide-react';

export function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Email, 2: 2FA, 3: New Password, 4: Success
  const [email, setEmail] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await api.request('/auth/password/reset-request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      
      setMessage(response.message);
      // In real implementation, check if user has 2FA enabled
      // For now, we'll simulate asking for 2FA code
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // In real implementation, verify 2FA code with backend
      // For now, simulate verification
      if (twoFactorCode.length === 6) {
        // Simulate getting reset token from email or backend
        setResetToken('simulated-reset-token');
        setStep(3);
      } else {
        setError('Please enter a valid 6-digit code');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.request('/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({
          token: resetToken,
          new_password: newPassword,
        }),
      });
      
      setStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 px-4">
        <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {step === 1 && 'Reset Your Password'}
            {step === 2 && 'Verify Your Identity'}
            {step === 3 && 'Create New Password'}
            {step === 4 && 'Password Reset Complete'}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 1 && 'Enter your email to receive reset instructions'}
            {step === 2 && 'Enter your 2FA code for additional security'}
            {step === 3 && 'Choose a strong new password'}
            {step === 4 && 'You can now log in with your new password'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {message && (
                <Alert>
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com"
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  We'll send a reset link to this email
                </p>
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          )}

          {/* Step 2: 2FA Verification */}
          {step === 2 && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <Alert>
                <Shield className="h-4 w-4" />
                <AlertDescription>
                  For your security, please verify your identity with your 2FA code.
                </AlertDescription>
              </Alert>
              
              <div>
                <label className="block text-sm font-medium mb-2">2FA Code</label>
                <Input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  required
                  placeholder="123456"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setStep(1)}
              >
                Back to email
              </Button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">New Password</label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">At least 8 characters</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="space-y-4 text-center">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="text-green-600" size={48} />
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Password Reset Successful!</h3>
                <p className="text-gray-600">
                  Your password has been changed. You can now log in with your new password.
                </p>
              </div>
              
              <Link to="/login">
                <Button className="w-full">Go to Login</Button>
              </Link>
            </div>
          )}
        </CardContent>
        {step !== 4 && (
          <CardFooter className="flex flex-col space-y-2 text-center text-sm">
            <Link to="/login" className="text-purple-600 hover:underline">
              Remember your password? Log in
            </Link>
            <Link to="/" className="text-gray-600 hover:underline">
              Back to home
            </Link>
          </CardFooter>
        )}
      </Card>
      </div>
    </Layout>
  );
}